import React, { useCallback, useMemo, useState } from "react";
import Link from "next/link";
import { IconButton, Tooltip } from "@material-ui/core";
import { Edit as EditIcon } from "@material-ui/icons";
import { DataTable } from "src/apps/commons/dataTable";
import { ImageAvatar } from "src/apps/commons/fileUploads";
import { formatDateTime } from "src/utils";
import { ActiveStatus } from "src/apps/commons/dataDisplays";
import { ListPaper } from "src/apps/commons/papers";
import * as Api from "../api";
import {
  IRemyndProduct,
  IRemyndProductPaginateReq,
  RemyndProductFilterTypes,
  RemyndProductPaginateOptionKey
} from "../types";
import { constants, paths } from "../utils";
import ListFilterPanel from "./ListFilterPanel";

const ListContainers: React.FC<unknown> = _ => {
  const [filters, setFilters] = useState<RemyndProductFilterTypes>({});

  const itemToString = useCallback(
    (data: IRemyndProduct) => `${data.code} : ${data.nameTh}`,
    []
  );

  const itemDeletable = useCallback(
    (data: IRemyndProduct) => !data.partnerProduct,
    []
  );

  const ManageCell = useCallback(
    (data: IRemyndProduct, isLoading: boolean) => (
      <div>
        {isLoading ? (
          <IconButton
            aria-label="Edit"
            key={`loading_manage_${data.id}`}
            disabled
          >
            <EditIcon />
          </IconButton>
        ) : (
          <Link href={`${paths.edit}?id=${data.id}`} key={`manage_${data.id}`}>
            <a>
              <IconButton aria-label="Edit">
                <Tooltip title={`แก้ไข ${constants.label}`} placement="top">
                  <EditIcon />
                </Tooltip>
              </IconButton>
            </a>
          </Link>
        )}
      </div>
    ),
    []
  );
  const ActiveCell = useCallback(
    (data: IRemyndProduct) => <ActiveStatus value={data.active} />,
    []
  );
  const SearchableCell = useCallback(
    (data: IRemyndProduct) => <ActiveStatus value={data.searchable} />,
    []
  );
  const CanceledCell = useCallback(
    (data: IRemyndProduct) => <ActiveStatus value={data.canceled} />,
    []
  );
  const ImageCell = useCallback((data: IRemyndProduct) => {
    return data.imageId ? (
      <ImageAvatar fileId={data.imageId} fit="contain" />
    ) : (
      <div />
    );
  }, []);

  const defaultCriteria = useMemo(
    () =>
      ({
        paginate: {
          page: 1,
          pageSize: 10,
          order: { optionKey: "UPDATED_AT", desc: true }
        }
      } as IRemyndProductPaginateReq),
    []
  );

  return (
    <>
      <ListFilterPanel onSubmit={v => setFilters(v)} />
      <ListPaper>
        <DataTable<
          IRemyndProduct,
          IRemyndProductPaginateReq,
          RemyndProductPaginateOptionKey,
          "MANAGE" | "IMAGE"
        >
          pathCreate={paths.create}
          searchTermLabel={"ค้นหาจาก รหัส, ชื่อ, แบรนด์, หมวดสินค้า"}
          listEntity={Api.list}
          deleteEntities={Api.deleteMultiple}
          itemDeletable={itemDeletable}
          itemToString={itemToString}
          defaultCriteria={defaultCriteria}
          filterValues={filters}
          columns={[
            {
              id: "MANAGE",
              label: "จัดการ",
              width: 60,
              style: { paddingLeft: 0, paddingRight: 8 },
              align: "center",
              renderCell: ManageCell
            },
            {
              id: "IMAGE",
              label: "รูป",
              width: 80,
              align: "center",
              renderCell: ImageCell
            },
            {
              id: "CODE",
              label: "รหัส",
              minWidth: 150,
              orderable: true,
              renderCell: data => data.code ?? ""
            },
            {
              id: "NAME_TH",
              label: "ชื่อ (TH)",
              minWidth: 300,
              orderable: true,
              renderCell: data => data.nameTh
            },
            {
              id: "NAME_EN",
              label: "ชื่อ (EN)",
              minWidth: 300,
              orderable: true,
              renderCell: data => data.nameEn
            },
            {
              id: "PARTNER",
              label: "Partner",
              minWidth: 150,
              orderable: true,
              renderCell: data => data.partner?.name ?? "ไม่ระบุ",
              renderFaded: data => !data.partner?.name
            },
            {
              id: "PARTNER_PRODUCT",
              label: "Partner Product",
              minWidth: 150,
              orderable: true,
              renderCell: data => data.partnerProduct?.nameEn ?? "ไม่ระบุ",
              renderFaded: data => !data.partnerProduct?.nameEn
            },
            {
              id: "BRAND",
              label: "แบรนด์",
              minWidth: 150,
              orderable: true,
              renderCell: data => data.brand?.nameTh ?? "ไม่ระบุ",
              renderFaded: data => !data.brand?.nameTh
            },
            {
              id: "CAT_LV1",
              label: "หมวดสินค้า Lv1",
              minWidth: 200,
              orderable: true,
              renderCell: data => data.categoryLv1?.nameTh ?? "ไม่ระบุ",
              renderFaded: data => !data.categoryLv1?.nameTh
            },
            {
              id: "CAT_LV2",
              label: "หมวดสินค้า Lv2",
              minWidth: 200,
              orderable: true,
              renderCell: data => data.categoryLv2?.nameTh ?? "ไม่ระบุ",
              renderFaded: data => !data.categoryLv2?.nameTh
            },
            {
              id: "CAT_LV3",
              label: "หมวดสินค้า Lv3",
              minWidth: 200,
              orderable: true,
              renderCell: data => data.categoryLv3?.nameTh ?? "ไม่ระบุ",
              renderFaded: data => !data.categoryLv3?.nameTh
            },
            {
              id: "CAT_TEXT_TH",
              label: "หมวดสินค้า (text) (TH)",
              minWidth: 220,
              orderable: true,
              renderCell: data => data.categoryTextTh ?? "ไม่ระบุ",
              renderFaded: data => !data.categoryTextTh
            },
            {
              id: "CAT_TEXT_EN",
              label: "หมวดสินค้า (text) (EN)",
              minWidth: 220,
              orderable: true,
              renderCell: data => data.categoryTextEn ?? "ไม่ระบุ",
              renderFaded: data => !data.categoryTextEn
            },
            {
              id: "SEARCHABLE",
              label: "Searchable",
              minWidth: 80,
              orderable: true,
              align: "center",
              renderCell: SearchableCell
            },
            {
              id: "ACTIVE",
              label: "Active",
              minWidth: 80,
              orderable: true,
              align: "center",
              renderCell: ActiveCell
            },
            {
              id: "CANCELED",
              label: "Canceled",
              minWidth: 80,
              orderable: true,
              align: "center",
              renderCell: CanceledCell
            },
            {
              id: "HIGHLIGHT_PRIORITY",
              label: "Highlight Priority",
              minWidth: 80,
              orderable: true,
              align: "right",
              renderCell: data => data.highlightPriority?.toString() ?? ""
            },
            {
              id: "CREATED_AT",
              label: "สร้างเมื่อ",
              width: 200,
              orderable: true,
              renderCell: data => formatDateTime(data.createdAt)
            },
            {
              id: "UPDATED_AT",
              label: "แก้ไขล่าสุดเมื่อ",
              width: 200,
              orderable: true,
              renderCell: data => formatDateTime(data.updatedAt)
            }
          ]}
        />
      </ListPaper>
    </>
  );
};

export default ListContainers;
